import { i18n } from "../../../../lib/globalization/i18n.js";
import { DataEndpoint } from "../../../../lib/data/remote/DataEndpoint.js";
import { Services } from "../../../../lib/services/Services.js";
import { Component } from "../../../../lib/ui/Component.js";
import { ComboBox } from "../../../../lib/ui/controls/ComboBox.js";
import { Button } from "../../../../lib/ui/controls/Button.js";
import { ModalDialog } from "../../../../lib/ui/controls/dialogs/ModalDialog.js";
import { Text } from "../../../../lib/ui/controls/Text.js";
import { Grid } from "../../../../lib/ui/controls/Grid.js";
import {
    FILE_REPOSITORY_PERMISSION_FIELDS,
    FILE_REPOSITORY_PERMISSIONS,
    hasFileRepositoryPermissionValue,
} from "../utils/file-repository-permissions.js";

const PERMISSION_LABELS = Object.freeze({
    open: 'Open',
    modify: 'Meta',
    writeItems: 'Soubory',
    writeFolders: 'Složky',
    alterPermissions: 'Alter',
    alterOwnership: 'Owner',
    grantPermissions: 'Grant',
});

const PERMISSION_ICONS = Object.freeze({
    open: 'folder-open',
    modify: 'pencil',
    writeItems: 'file-plus',
    writeFolders: 'folder-plus',
    alterPermissions: 'circle-user-circle-check',
    alterOwnership: 'user-gear',
    grantPermissions: 'shield-user',
});

export class FolderPermissionPanel extends Component {

    static PERMISSIONS = FILE_REPOSITORY_PERMISSIONS;
    static PERMISSION_FIELDS = FILE_REPOSITORY_PERMISSION_FIELDS;

    constructor(parent, options = {}) {

        super(parent, options);

        const {
            getFolderData = () => null,
            getFileRepositoryManager = () => null,
            onOpenFolder = () => null,
            onReloadRequested = async () => null,
        } = options || {};

        this.getFolderDataValue = getFolderData;
        this.getFileRepositoryManagerValue = getFileRepositoryManager;
        this.onOpenFolderValue = onOpenFolder;
        this.onReloadRequestedValue = onReloadRequested;

        this.permissionContext = null;
        this.permissionRows = [];
        this.permissionLoadError = null;
        this.permissionDrafts = {};
        this.permissionUsers = [];
        this.permissionRoles = [];

    };

    getFolderData() {
        return typeof this.getFolderDataValue === 'function' ? this.getFolderDataValue.call(this) : null;
    };

    getFileRepositoryManager() {
        return typeof this.getFileRepositoryManagerValue === 'function' ? this.getFileRepositoryManagerValue.call(this) : null;
    };

    async reloadOwnerModule() {
        if (typeof this.onReloadRequestedValue === 'function')
            return this.onReloadRequestedValue.call(this);
        return null;
    };

    openFolder(folderUid) {
        if (typeof this.onOpenFolderValue === 'function')
            this.onOpenFolderValue.call(this, folderUid);
    };

    handleError(error) {
        this.getApp().handleError(error);
    };

    getPermissionContext() {
        return this.permissionContext || null;
    };

    getPermissionRows() {
        return Array.isArray(this.permissionRows) ? this.permissionRows : [];
    };

    canShowPermissionEditor() {
        return !!this.getPermissionContext()?.ownsPermissionContext;
    };

    canDetachPermissions() {
        return !!this.getPermissionContext()?.canDetachPermissions;
    };

    canInheritPermissions() {
        return !!this.getPermissionContext()?.canInheritPermissions;
    };

    canNavigateToPermissionOwner() {
        const context = this.getPermissionContext();
        return !!context?.permissionFolderUid && context.permissionFolderUid !== this.getFolderData()?.uid;
    };

    canEditPermissionRows() {
        const context = this.getPermissionContext();
        return !!context?.ownsPermissionContext && (!!context?.canAlterPermissions || !!context?.canGrantPermissions);
    };

    getPermissionEditableMask() {
        const context = this.getPermissionContext();
        let mask = 0;

        if (context?.canAlterPermissions) {
            mask |= FolderPermissionPanel.PERMISSIONS.open
                | FolderPermissionPanel.PERMISSIONS.modify
                | FolderPermissionPanel.PERMISSIONS.writeItems
                | FolderPermissionPanel.PERMISSIONS.writeFolders;
        }

        if (context?.canGrantPermissions) {
            mask |= FolderPermissionPanel.PERMISSIONS.alterPermissions
                | FolderPermissionPanel.PERMISSIONS.grantPermissions
                | FolderPermissionPanel.PERMISSIONS.alterOwnership;
        }

        return mask;
    };

    canEditPermissionBit(permissionBit) {
        return hasFileRepositoryPermissionValue(this.getPermissionEditableMask(), permissionBit);
    };

    getPermissionDraft(rowId) {
        return this.permissionDrafts?.[rowId] || null;
    };

    isPermissionRowDirty(rowId) {
        return !!this.getPermissionDraft(rowId);
    };

    getPermissionRowPermissions(row) {
        return this.getPermissionDraft(row?.id)?.permissions ?? row?.permissions ?? 0;
    };

    getPermissionLoadErrorSubtitle() {

        const error = this.permissionLoadError;
        const details = error?.details || error?.error || error || null;
        const detailMessage = details?.description || details?.message || null;

        if (detailMessage && detailMessage !== 'RemoteDataService call error')
            return detailMessage;

        if (error?.message && error.message !== 'RemoteDataService call error')
            return error.message;

        return 'Správu oprávnění se teď nepodařilo načíst.';

    };

    updatePermissionDraft(row, nextPermissions) {
        if (!row?.id)
            return;

        const normalizedPermissions = Number(nextPermissions) || 0;
        const currentPermissions = Number(row.permissions) || 0;

        if (normalizedPermissions === currentPermissions) {
            const nextDrafts = { ...this.permissionDrafts };
            delete nextDrafts[row.id];
            this.permissionDrafts = nextDrafts;
        } else {
            this.permissionDrafts = {
                ...this.permissionDrafts,
                [row.id]: {
                    id: row.id,
                    permissions: normalizedPermissions,
                },
            };
        }

        this.invalidate();
    };

    togglePermissionRowBit(row, permissionBit) {
        if (!this.canEditPermissionBit(permissionBit))
            return;

        const currentPermissions = this.getPermissionRowPermissions(row);
        const nextPermissions = hasFileRepositoryPermissionValue(currentPermissions, permissionBit)
            ? (currentPermissions & ~permissionBit)
            : (currentPermissions | permissionBit);

        this.updatePermissionDraft(row, nextPermissions);
    };

    cancelPermissionRowChanges(rowId) {
        if (!this.permissionDrafts[rowId])
            return;

        const nextDrafts = { ...this.permissionDrafts };
        delete nextDrafts[rowId];
        this.permissionDrafts = nextDrafts;
        this.invalidate();
    };

    resetPermissionEditorState() {
        this.permissionContext = null;
        this.permissionRows = [];
        this.permissionLoadError = null;
        this.permissionDrafts = {};
    };

    async ensurePermissionSubjectsLoaded() {

        const authManager = this.getApp()?.getAuthManager?.() || null;

        if (!Array.isArray(this.permissionUsers) || this.permissionUsers.length === 0) {
            const userEndpoint = new DataEndpoint('core/users/codelist', authManager);
            this.permissionUsers = [...((await userEndpoint.get()) || [])];
        }

        if (!Array.isArray(this.permissionRoles) || this.permissionRoles.length === 0) {
            const roleEndpoint = new DataEndpoint('core/roles/codelist', authManager);
            this.permissionRoles = [...((await roleEndpoint.get()) || [])];
        }

    };

    async savePermissionRow(row) {

        if (!row?.id || !this.isPermissionRowDirty(row.id))
            return;

        const folderUid = this.getFolderData()?.uid || null;
        if (!folderUid) {
            this.handleError('Aktuální složka není k dispozici pro uložení oprávnění.');
            return;
        }

        const app = this.getApp();
        app?.setBusyFlag?.();

        try {
            await this.getFileRepositoryManager().updateFolderPermission(folderUid, row.id, {
                permissions: this.getPermissionDraft(row.id)?.permissions ?? row.permissions ?? 0,
            });
            await this.reloadOwnerModule();
        } catch (error) {
            app?.handleError?.(error);
        } finally {
            app?.clearBusyFlag?.();
        }

    };

    async deletePermissionRow(row) {

        if (!row?.id)
            return;

        const folderUid = this.getFolderData()?.uid || null;
        if (!folderUid) {
            this.handleError('Aktuální složka není k dispozici pro smazání oprávnění.');
            return;
        }

        const app = this.getApp();
        app?.setBusyFlag?.();

        try {
            await this.getFileRepositoryManager().deleteFolderPermission(folderUid, row.id);
            await this.reloadOwnerModule();
        } catch (error) {
            app?.handleError?.(error);
        } finally {
            app?.clearBusyFlag?.();
        }

    };

    async detachPermissions() {
        return this.runPermissionContextAction('detach');
    };

    async inheritPermissions() {
        return this.runPermissionContextAction('inherit');
    };

    async runPermissionContextAction(action) {

        const folderUid = this.getFolderData()?.uid || null;
        if (!folderUid) {
            this.handleError('Aktuální složka není k dispozici pro správu oprávnění.');
            return;
        }

        const app = this.getApp();
        app?.setBusyFlag?.();

        try {
            if (action === 'detach')
                await this.getFileRepositoryManager().detachFolderPermissions(folderUid);
            else if (action === 'inherit')
                await this.getFileRepositoryManager().inheritFolderPermissions(folderUid);
            await this.reloadOwnerModule();
        } catch (error) {
            app?.handleError?.(error);
        } finally {
            app?.clearBusyFlag?.();
        }

    };

    async showCreatePermissionDialog(subjectType = 'user') {

        const folderUid = this.getFolderData()?.uid || null;
        if (!folderUid) {
            this.handleError('Aktuální složka není k dispozici pro přidání oprávnění.');
            return;
        }

        await this.ensurePermissionSubjectsLoaded();

        const app = this.getApp();
        const control = this;
        const subjectItems = subjectType === 'role' ? this.permissionRoles : this.permissionUsers;
        const subjectLabel = subjectType === 'role' ? 'Role' : 'Uživatel';
        const subjectProp = subjectType === 'role' ? 'roleId' : 'userId';
        const subjectCombo = new ComboBox(null, {
            value: null,
            items: () => subjectItems,
            itemValueProp: 'value',
            itemLabelProp: 'label',
            emptyOptionLabel: `Vyber ${subjectType === 'role' ? 'roli' : 'uživatele'}`,
        });

        const modal = new ModalDialog(null, {
            title: subjectType === 'role' ? 'Přidat roli' : 'Přidat uživatele',
            controls: [
                (
                    new Grid(null, {})
                        .cell({ controls: [new Text(null, { text: `Vyber ${subjectType === 'role' ? 'roli' : 'uživatele'}, kterému chceš založit grant.` })] })
                        .cell({ controls: [new Text(null, { text: subjectLabel })] })
                        .cell({ controls: [subjectCombo] })
                )
            ],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: 'cancel',
                    css: '.text',
                    onClick: () => { modal.hide(); }
                }),
                new Button(null, {
                    label: 'Přidat',
                    icon: 'confirm',
                    onClick: createConfirmed
                }),
            ]
        });

        modal.show(app);

        async function createConfirmed() {

            const subjectValue = subjectCombo.getValue();
            if (!subjectValue) {
                control.handleError(`Vyber ${subjectType === 'role' ? 'roli' : 'uživatele'}.`);
                return;
            }

            modal.hide();
            app?.setBusyFlag?.();

            try {
                await control.getFileRepositoryManager().createFolderPermission(folderUid, {
                    [subjectProp]: subjectValue,
                    permissions: 0,
                });
                await control.reloadOwnerModule();
            } catch (error) {
                app?.handleError?.(error);
            } finally {
                app?.clearBusyFlag?.();
            }

        }

    };

    async _refresh(options, activeRoute) {

        await super._refresh(options, activeRoute);

        const folderUid = this.getFolderData()?.uid || null;
        if (!folderUid) {
            this.resetPermissionEditorState();
            return true;
        }

        this.permissionLoadError = null;

        try {
            const [context, dataset] = await Promise.all([
                this.getFileRepositoryManager().getFolderPermissionContext(folderUid),
                this.getFileRepositoryManager().getFolderPermissions(folderUid),
            ]);

            this.permissionContext = context || null;
            this.permissionRows = Array.isArray(dataset?.data) ? dataset.data : [];
            this.permissionDrafts = {};

            return true;
        } catch (error) {
            this.permissionContext = null;
            this.permissionRows = [];
            this.permissionDrafts = {};
            this.permissionLoadError = error;
            return false;
        }

    };

    _render(vm) {

        const ct = Services.get('templates');
        const control = this;

        if (!this.getFolderData())
            return ct.div();

        return ct.section([
            renderPermissionHeader(),
            ...renderPermissionBody(),
        ]);

        function renderPermissionHeader() {
            return ct.row('.border.medium-round.medium-padding.wrap', [
                ct.span('.bold', ['Oprávnění složky']),
                ' ',
                ct.span('.small', [
                    controlStatusLabel()
                ]),
            ]);
        }

        function controlStatusLabel() {
            const context = control.getPermissionContext();
            if (!context)
                return 'nejsou dostupná';
            if (context.ownsPermissionContext)
                return `spravována zde: ${context.permissionFolderName || context.folderName}`;
            return `jsou převzata ze složky "${context.permissionFolderName || '?'}"`;
        }

        function renderPermissionBody() {
            if (control.permissionLoadError) {
                return ct.table('.lined', [
                    ct.tbody([
                        ct.trow([
                            ct.tcell({ colspan: 10 }, [
                                ct.empty({
                                    title: 'Oprávnění se nepodařilo načíst',
                                    subtitle: control.getPermissionLoadErrorSubtitle(),
                                    icon: ct.icon('lock', '.fa-3x'),
                                }),
                            ]),
                        ]),
                    ]),
                ]);
            }

            const blocks = [
                renderPermissionToolbar(),
            ];

            if (control.canShowPermissionEditor()) {
                blocks.push(renderPermissionTable());
            } else {
                blocks.push(renderSharedPermissionInfo());
            }

            return blocks;
        }

        function renderPermissionToolbar() {
            const canEditRows = control.canEditPermissionRows();

            return ct.nav([
                control.canDetachPermissions()
                    ? ct.button('Odpojit oprávnění', 'link-broken', '.outlined', {
                        onclick: () => { control.detachPermissions(); }
                    })
                    : null,
                control.canInheritPermissions()
                    ? ct.button('Zdědit oprávnění', 'link', '.outlined', {
                        onclick: () => { control.inheritPermissions(); }
                    })
                    : null,
                control.canNavigateToPermissionOwner()
                    ? ct.button('Přejít na vlastníka oprávnění', 'arrow-up-to-dotted-line', '.outlined', {
                        onclick: () => { control.openFolder(control.getPermissionContext()?.permissionFolderUid || null); }
                    })
                    : null,
                ct.navgap(),
                control.canShowPermissionEditor()
                    ? ct.button('Přidat uživatele', 'user', {
                        disabled: !canEditRows,
                        title: canEditRows ? null : 'Nemáš oprávnění upravovat granty v této složce.',
                        onclick: () => { control.showCreatePermissionDialog('user'); }
                    })
                    : null,
                control.canShowPermissionEditor()
                    ? ct.button('Přidat roli', 'users', {
                        disabled: !canEditRows,
                        title: canEditRows ? null : 'Nemáš oprávnění upravovat granty v této složce.',
                        onclick: () => { control.showCreatePermissionDialog('role'); }
                    })
                    : null,
            ]);
        }

        function renderSharedPermissionInfo() {
            const context = control.getPermissionContext();
            return ct.table('.lined', [
                ct.tbody([
                    ct.trow([
                        ct.tcell({ colspan: 10 }, [
                            ct.empty({
                                title: 'Tato složka sdílí oprávnění',
                                subtitle: context?.permissionFolderName
                                    ? `Oprávnění složky jsou převzata ze složky "${context.permissionFolderName}".`
                                    : 'Oprávnění složky jsou převzata z jiné složky.',
                                icon: ct.icon('link', '.fa-3x'),
                            }),
                        ]),
                    ]),
                ]),
            ]);
        }

        function renderPermissionTable() {
            return ct.table('.lined', [
                ct.thead([
                    ct.trow([
                        ct.thcell([ct.icon('users'), ' Role / ', ct.icon('user'), ' Uživatel']),
                        ...FolderPermissionPanel.PERMISSION_FIELDS.map((field) => ct.thcell('.min.center-align', [
                            field.key ? ct.icon(PERMISSION_ICONS[field.key], '.fa-2x') : field.label
                        ])),
                        ct.thcell('.min.right-align', ['Akce']),
                    ]),
                ]),
                ct.tbody(
                    control.getPermissionRows().map((row) => renderPermissionRow(row))
                ),
            ]);
        }

        function renderPermissionRow(row) {
            const rowPermissions = control.getPermissionRowPermissions(row);
            const rowDirty = control.isPermissionRowDirty(row.id);

            return ct.trow([
                ct.tcell([
                    (row.roleName ? ct.icon('users') : (row.userLabel ? ct.icon('user') : '')),
                    ' ',
                    (row.roleName || row.userLabel || '-')

                ]),
                ...FolderPermissionPanel.PERMISSION_FIELDS.map((field) => {
                    const checked = hasFileRepositoryPermissionValue(rowPermissions, field.value);
                    const disabled = !control.canEditPermissionBit(field.value);

                    return ct.tcell('.min.center-align', [
                        ct.checkBox(checked, '', '.inline-block', {
                            text: '',
                            disabled,
                            title: disabled ? 'Tento bit oprávnění teď nemůžeš měnit.' : null,
                            onchange: () => { control.togglePermissionRowBit(row, field.value); }
                        }),
                    ]);
                }),
                ct.tcell('.min.right-align', [
                    rowDirty
                        ? ct.iconButton('confirm', '.transparent.circle', {
                            title: 'Uložit změny',
                            onclick: () => { control.savePermissionRow(row); }
                        })
                        : null,
                    rowDirty
                        ? ct.iconButton('cancel', '.transparent.circle', {
                            title: 'Zrušit změny',
                            onclick: () => { control.cancelPermissionRowChanges(row.id); }
                        })
                        : null,
                    !rowDirty
                        ? ct.iconButton('delete', '.transparent.circle', {
                            title: 'Odstranit grant',
                            disabled: !control.canEditPermissionRows(),
                            onclick: () => { control.deletePermissionRow(row); }
                        })
                        : null,
                ]),
            ]);
        }

    }

}
